import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TooltipModel,
} from 'chart.js';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useContext } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import type { AllocationOfCostsChartData } from '../../api/financials-client/financials-client.type';
import { ViewBy } from '../../api/financials-client/financials-client.type';
import { ChartBox } from '../chart-styles/chart-styles';
import { BoardContext, TeamContext } from '../../contexts';
import { formatCurrencyAxisTick, getCurrency } from '../../helpers/currency-helpers';
import { safeParseDateInput } from '../../helpers/date-time-helpers/parsing/parsing';
import { useGlobalStore } from '../../store/global-store/global-store';
import { allocationOfCostsTooltipHandler } from './allocation-of-costs-tooltip';
import type { ShowAllocationOfCostsChartLine } from './allocation-of-costs.type';

dayjs.extend(customParseFormat);

// Register chart.js plugins needed for the chart
ChartJS.register(BarElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

type Position = 'left' | 'right' | 'center' | 'top' | 'bottom';

/**
 * Format the data according to the chart.js requirements
 * @param data
 * @returns formatted data
 */
const formatData = (
  data: SanitizedData,
  showAllocationOfCostsChartLines: ShowAllocationOfCostsChartLine[],
): ChartData<'bar'> => {
  const dataset: ChartData<'bar'> = {
    labels: data.labels,
    datasets: [],
  };

  const chartDataByField: { [id: string]: number[] } = {};
  for (const [index, chartData] of data.chartData.entries()) {
    for (const [field, value] of Object.entries(chartData)) {
      const curr = chartDataByField[field] || Array.from({ length: data.labels.length }).fill(0);
      curr[index] = value;
      chartDataByField[field] = curr;
    }
  }
  for (const field in chartDataByField) {
    const fieldData = showAllocationOfCostsChartLines.find((line) => (line.field === field ? line.show : false));
    if (fieldData?.show || false) {
      dataset.datasets.push({
        label: field,
        data: chartDataByField[field],
        backgroundColor: fieldData.color,
        borderColor: fieldData.color,
        borderWidth: 0,
        yAxisID: 'y',
      });
    }
  }
  return dataset;
};

const getChartData = (data: AllocationOfCostsChartData, viewBy: ViewBy): { [option: string]: number }[] => {
  const chartData: { [K in ViewBy]: Array<{ [key: string]: number }> } = {
    tasks_in_usd: [],
    points_in_usd: [],
    tasks: [],
    points: [],
  };

  Object.values(data).forEach((timeAllocation) => {
    const tasksInUsd: { [key: string]: number } = {};
    const pointsInUsd: { [key: string]: number } = {};
    const tasks: { [key: string]: number } = {};
    const points: { [key: string]: number } = {};
    Object.entries(timeAllocation).forEach(([factorOption, factorData]) => {
      tasksInUsd[factorOption] = factorData.total.tasks_in_usd;
      pointsInUsd[factorOption] = factorData.total.points_in_usd;
      tasks[factorOption] = factorData.total.tasks;
      points[factorOption] = factorData.total.points;
    });
    chartData.tasks_in_usd.push(tasksInUsd);
    chartData.points_in_usd.push(pointsInUsd);
    chartData.tasks.push(tasks);
    chartData.points.push(points);
  });

  return chartData[viewBy] || [];
};

const mapToSanitizedData = (
  data: AllocationOfCostsChartData,
  viewBy: ViewBy,
  isWorkPeriods: boolean,
): SanitizedData => {
  const dataSortedByYearAndMonth = Object.entries(data).sort((a, b) => {
    // Check if the keys are in YYYY-MM format
    const parsedDateA = safeParseDateInput(a[0]);
    const parsedDateB = safeParseDateInput(b[0]);
    if (parsedDateA && parsedDateB) {
      return parsedDateA.diff(parsedDateB);
    }
    // If not in YYYY-MM format, maintain original order
    return 0;
  });

  const labels = dataSortedByYearAndMonth.map(([key, _value]) => {
    if (isWorkPeriods) {
      return key;
    }
    const parsedDate = safeParseDateInput(key);
    if (parsedDate) {
      return parsedDate.format('MMM YY');
    }
    return key;
  });

  const chartData = getChartData(Object.fromEntries(dataSortedByYearAndMonth), viewBy);

  return {
    labels,
    chartData,
  };
};

type SanitizedData = {
  labels: string[];
  chartData: { [id: string]: number }[];
};

type AllocationOfCostsChartProps = {
  showAllocationOfCostsChartLines: ShowAllocationOfCostsChartLine[];
  chartData: AllocationOfCostsChartData | null;
  viewBy: ViewBy;
  category: string;
  portfolioId: string | undefined;
  workPeriodIds: string[];
};

export const AllocationOfCostsChart = ({
  showAllocationOfCostsChartLines,
  chartData,
  viewBy,
  category,
  portfolioId,
  workPeriodIds,
}: AllocationOfCostsChartProps) => {
  const { t } = useTranslation();
  const { team } = useContext(TeamContext);
  const { board } = useContext(BoardContext);
  const isCost = viewBy === ViewBy.TASKS_IN_USD || viewBy === ViewBy.POINTS_IN_USD;
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode, symbol: currencySymbol } = getCurrency(organization);
  const yAxisLabel = isCost ? currencyCode : 'Units';
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    pointStyle: false,
    cubicInterpolationMode: 'monotone',
    borderWidth: 5,
    aspectRatio: 2.5,
    layout: {
      padding: {
        top: 20,
        right: 0,
      },
    },

    scales: {
      x: {
        title: {
          display: true,
          text: workPeriodIds.length > 0 ? 'Sprint' : 'Month',
          font: {
            size: 16,
          },
        },
        ticks: {
          font: {
            size: 14,
          },
        },
        grid: {
          display: false,
        },
        stacked: true,
      },
      y: {
        beginAtZero: true,
        position: 'left' as Position,
        title: {
          display: true,
          text: yAxisLabel,
          font: {
            size: 16,
          },
        },
        ticks: {
          font: {
            size: 14,
          },
          callback: (value: number | string) => (isCost ? formatCurrencyAxisTick(value, currencySymbol) : `${value}`),
        },
        stacked: true,
      },
    },

    plugins: {
      legend: {
        display: false,
      },
      annotation: {
        common: {
          drawTime: 'afterDraw',
        },
      },
      tooltip: {
        enabled: false,
        external: (context: { chart: ChartJS; tooltip: TooltipModel<'bar'> }) =>
          allocationOfCostsTooltipHandler(
            context,
            chartData,
            viewBy,
            category,
            portfolioId,
            team?.id,
            board?.id,
            board?.name,
            workPeriodIds,
            t,
          ),
      },
      filler: {
        propagate: true,
        drawTime: 'beforeDatasetsDraw' as const,
      },
    },
  };

  return (
    <ChartBox>
      {chartData && Object.keys(chartData).length > 0 ? (
        <Bar
          data={formatData(
            mapToSanitizedData(chartData, viewBy, workPeriodIds.length > 0),
            showAllocationOfCostsChartLines,
          )}
          options={options}
        />
      ) : (
        <Bar
          data={{
            labels: ['No data available'],
            datasets: [],
          }}
          options={options}
        />
      )}
    </ChartBox>
  );
};
